import React, { Children, useEffect, useState } from 'react';
import { Checkbox, DraggableList, TextField } from '@biq/apex-ui';
import { Grid, Icon, InputAdornment, SxProps, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    FieldErrors,
    UseFormClearErrors,
    UseFormSetValue,
} from 'react-hook-form';
import {
    attributesComponentStyles,
    getAttributesContainerHeight,
} from './attributes-component-helper';

interface AttributesComponentProps {
    attributesData: string[];
    selectedAttributesData: string[] | undefined;
    setValue: UseFormSetValue<any>;
    clearErrors: UseFormClearErrors<any>;
    errors: FieldErrors<any>;
    errorText?: string;
    setIsValuesChanged: React.Dispatch<React.SetStateAction<boolean>>;
    required?: boolean;
    attributeKey?: string;
    customAttributesSelectedListContainerStyles?: SxProps;
    customAttributesMainListContainerStyles?: SxProps;
}

export const AttributesComponent = ({
    attributesData,
    selectedAttributesData,
    setValue,
    clearErrors,
    errors,
    errorText,
    setIsValuesChanged,
    required = false,
    attributeKey = 'attributes',
    customAttributesSelectedListContainerStyles,
    customAttributesMainListContainerStyles,
}: AttributesComponentProps): React.JSX.Element => {
    const [attributesFieldValue, setAttributesFieldValue] =
        useState<string>('');
    const [attributesSelectedDataArr, setAttributesSelectedDataArr] = useState<
        string[]
    >([]);
    const [attributesUnselectedDataArr, setAttributesUnselectedDataArr] =
        useState<string[]>([]);
    const [filteredSelectedDataArr, setFilteredSelectedDataArr] = useState<
        string[]
    >([]);
    const [filteredUnselectedDataArr, setFilteredUnselectedDataArr] = useState<
        string[]
    >([]);
    const [draggableListArr, setDraggableListArr] = useState<
        {
            id: string | number;
            item: string | React.JSX.Element;
        }[]
    >([]);
    const [isSearchEnabled, setIsSearchEnabled] = useState<boolean>(false);

    const handleCheckBoxClick = (checked: boolean, attribute: string): void => {
        setIsValuesChanged(true);
        clearErrors(attributeKey);
        let updatedAttributesSelectedDataArr = attributesSelectedDataArr;
        let updatedAttributesUnselectedDataArr = attributesUnselectedDataArr;
        if (!checked) {
            if (updatedAttributesSelectedDataArr.length) {
                updatedAttributesSelectedDataArr =
                    updatedAttributesSelectedDataArr.filter((item) => {
                        if (item !== attribute) {
                            return true;
                        } else {
                            updatedAttributesUnselectedDataArr.push(item);
                            return false;
                        }
                    });
            }
        }
        if (checked) {
            if (updatedAttributesUnselectedDataArr.length) {
                updatedAttributesUnselectedDataArr =
                    updatedAttributesUnselectedDataArr.filter((item) => {
                        if (item !== attribute) {
                            return true;
                        } else {
                            updatedAttributesSelectedDataArr.push(item);
                            return false;
                        }
                    });
            }
        }
        setAttributesFieldValue('');
        setValue(attributeKey, updatedAttributesSelectedDataArr);
        setAttributesSelectedDataArr(updatedAttributesSelectedDataArr);
        setAttributesUnselectedDataArr(updatedAttributesUnselectedDataArr);
        setFilteredSelectedDataArr(updatedAttributesSelectedDataArr);
        setFilteredUnselectedDataArr(updatedAttributesUnselectedDataArr);
    };

    useEffect(() => {
        if (draggableListArr.length) {
            const orderedAttributes = draggableListArr.map((item: any) => {
                return item?.item?.props?.label;
            });
            let itemsOrderChanges: boolean = false;
            attributesSelectedDataArr.forEach((item: string, index: number) => {
                const findIndex = orderedAttributes.findIndex(
                    (orderedItem) => orderedItem === item
                );
                if (findIndex > -1) {
                    itemsOrderChanges = index !== findIndex;
                }
            });
            if (itemsOrderChanges) {
                setAttributesSelectedDataArr(orderedAttributes);
                setFilteredSelectedDataArr(orderedAttributes);
                setValue(attributeKey, orderedAttributes);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draggableListArr]);

    useEffect(() => {
        if (attributesData.length) {
            if (selectedAttributesData?.length) {
                setAttributesSelectedDataArr(selectedAttributesData);
                setFilteredSelectedDataArr(selectedAttributesData);
                let updatedAttributesUnselectedDataArr: string[] = [];
                attributesData.forEach((attribute: string) => {
                    if (!selectedAttributesData.includes(attribute)) {
                        updatedAttributesUnselectedDataArr.push(attribute);
                    }
                });
                setAttributesUnselectedDataArr(
                    updatedAttributesUnselectedDataArr
                );
                setFilteredUnselectedDataArr(
                    updatedAttributesUnselectedDataArr
                );
            } else {
                setAttributesSelectedDataArr([]);
                setAttributesUnselectedDataArr(attributesData);
                setFilteredSelectedDataArr([]);
                setFilteredUnselectedDataArr(attributesData);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributesData, selectedAttributesData]);

    useEffect(() => {
        if (filteredSelectedDataArr?.length) {
            const selectedDataArr = [
                ...(isSearchEnabled
                    ? filteredSelectedDataArr
                    : attributesSelectedDataArr),
            ];
            let arr: {
                id: string | number;
                item: string | React.JSX.Element;
            }[] = [];
            selectedDataArr.forEach((itemData, index) =>
                arr.push({
                    id: index + 1,
                    item: (
                        <Checkbox
                            label={itemData}
                            checked
                            onChange={(e): void =>
                                handleCheckBoxClick(e.target.checked, itemData)
                            }
                        />
                    ),
                })
            );
            setDraggableListArr(arr);
        } else {
            setDraggableListArr([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        attributesSelectedDataArr,
        attributesUnselectedDataArr,
        filteredSelectedDataArr,
        filteredUnselectedDataArr,
    ]);

    useEffect(() => {
        if (attributesFieldValue) {
            setIsSearchEnabled(true);
            let selectedDataArr = [...attributesSelectedDataArr];
            let unSelectedDataArr = [...attributesUnselectedDataArr];
            const filteredSelected = selectedDataArr?.filter((attribute) =>
                attribute
                    .toLowerCase()
                    .includes(attributesFieldValue.toLowerCase())
            );
            const filteredUnselected = unSelectedDataArr?.filter((attribute) =>
                attribute
                    .toLowerCase()
                    .includes(attributesFieldValue.toLowerCase())
            );
            setFilteredSelectedDataArr(filteredSelected);
            setFilteredUnselectedDataArr(filteredUnselected);
        } else {
            setIsSearchEnabled(false);
            setFilteredSelectedDataArr(attributesSelectedDataArr);
            setFilteredUnselectedDataArr(attributesUnselectedDataArr);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        attributesFieldValue,
        attributesSelectedDataArr,
        attributesUnselectedDataArr,
    ]);

    const handleAttributesFieldChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        setAttributesFieldValue(e.target.value);
    };

    const emptyStateCondition = isSearchEnabled
        ? Boolean(
              filteredSelectedDataArr.length || filteredUnselectedDataArr.length
          )
        : Boolean(
              attributesSelectedDataArr.length ||
                  attributesUnselectedDataArr.length
          );

    return (
        <Grid>
            <Grid sx={attributesComponentStyles.fieldStyles}>
                <TextField
                    variant='outlined'
                    label={'Attributes'}
                    value={attributesFieldValue}
                    onChange={handleAttributesFieldChange}
                    required={required}
                    error={!!errors.attributes}
                    errorText={errorText}
                    helperText={
                        required ? 'At least one attribute is required' : ''
                    }
                    InputProps={{
                        startAdornment: [
                            <InputAdornment key={'searchIcon'} position='start'>
                                <SearchIcon
                                    sx={
                                        attributesComponentStyles.searchIconStyles
                                    }
                                />
                            </InputAdornment>,
                        ],
                        endAdornment: attributesFieldValue.length
                            ? [
                                  <Icon
                                      key={'clearIcon'}
                                      fontSize='inherit'
                                      color='disabled'
                                      sx={
                                          attributesComponentStyles.crossIconStyles
                                      }
                                      onClick={(): void => {
                                          setAttributesFieldValue('');
                                          setIsSearchEnabled(false);
                                      }}
                                  >
                                      <CancelIcon />
                                  </Icon>,
                              ]
                            : null,
                    }}
                />
            </Grid>
            <Grid
                sx={
                    customAttributesMainListContainerStyles ?? {
                        ...attributesComponentStyles.attributesListMainContainerStyles,
                        height: getAttributesContainerHeight(errors),
                    }
                }
            >
                <Grid
                    sx={attributesComponentStyles.attributesListContainerStyles}
                >
                    {emptyStateCondition ? (
                        <>
                            <Grid
                                sx={
                                    customAttributesSelectedListContainerStyles ??
                                    attributesComponentStyles.attributesSelectedListContainerStyles
                                }
                            >
                                <DraggableList
                                    listItems={draggableListArr}
                                    setListItems={setDraggableListArr}
                                    isMenuEnabled
                                    customStyles={
                                        attributesComponentStyles.attributesDraggableListContainerStyles
                                    }
                                />
                            </Grid>
                            <Grid
                                sx={
                                    attributesComponentStyles.attributesUnselectedListContainerStyles
                                }
                            >
                                {Children.toArray(
                                    filteredUnselectedDataArr?.map(
                                        (attribute: string) => {
                                            return (
                                                <Checkbox
                                                    label={attribute}
                                                    checked={false}
                                                    onChange={(e): void =>
                                                        handleCheckBoxClick(
                                                            e.target.checked,
                                                            attribute
                                                        )
                                                    }
                                                />
                                            );
                                        }
                                    )
                                )}
                            </Grid>
                        </>
                    ) : (
                        <Grid
                            sx={
                                attributesComponentStyles.attributesListEmptyContainerStyles
                            }
                        >
                            <Typography
                                sx={
                                    attributesComponentStyles.attributesListEmptyContainerTextStyles
                                }
                            >
                                No results matching that criteria.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};
